import React, { useState, useEffect, useCallback } from "react";
import {
  CircularProgress,
  Container,
  TableContainer,
  Table,
  TableHead,
  TableCell,
  TableRow,
  TableBody,
  Typography,
  Button,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Chip,
  Stack,
  Link,
  IconButton,
  Autocomplete,
  TextField,
} from "@mui/material";

import { backend, API_URL } from "../../../config";

import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import UploadFile from "../components/UploadFile";

import readXlsxFile from "read-excel-file";
import { useDispatch, useSelector } from "react-redux";

import { fetch_products_data_action } from "../../../redux/actions/products";

export const ParStock = (props) => {
  const dispatch = useDispatch();
  const productsStore = useSelector((state) => state.products || []);

  const [errorInfo, setErrorInfo] = useState({ error: false, message: "" });
  const [fileData, setFileData] = useState([]);
  const [fileProcessing, setFileProcessing] = useState(false);
  const [comparisonResults, setComparisonResults] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [filters, setFilters] = useState({
    lowStock: false,
    existing: false,
    warehouse: null,
  });

  const handleChangeData = async (data, file) => {
    setFileProcessing(true);
    let dataImported = [];

    switch (file.type) {
      case "text/csv":
        data
          .map((item) => item.data)
          .forEach((item) => {
            if (item.length > 1) {
              dataImported.push(item);
            }
          });

        break;
      case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
        await readXlsxFile(file).then((data) => {
          data.forEach((item) => {
            if (item.length > 1) {
              dataImported.push(item);
            }
          });
        });

        break;
      default:
        setErrorInfo({
          error: true,
          message: "Unsupported file type. Please usload a .csv or .xlsx file.",
        });
    }

    if (!dataImported.length) {
      setErrorInfo({
        error: true,
        message:
          "The imported file couldn't be processed (it didn't generate any data to be imported).",
      });
      return;
    } else {
      const headersIndex = dataImported.findIndex((item) =>
        String(item).toLowerCase().includes("quantity on hand"),
      );

      if (headersIndex === -1) {
        setErrorInfo({
          error: true,
          message:
            "The imported file couldn't be processed (couldn't find the headers from the imported data: quantity on hand).",
        });
        return;
      }

      const headersRow = dataImported[headersIndex].map((item) =>
        String(item || "")
          .trim()
          .toLowerCase(),
      );

      const headersMap = {
        item_code: headersRow.indexOf("item"),
        description: headersRow.indexOf("description"),
        quantity_on_hand: headersRow.indexOf("quantity on hand"),
        uom: headersRow.indexOf("u/m"),
      };

      const rowsMap = dataImported.slice(headersIndex + 1).map((row) => ({
        item_code: String(row[headersMap.item_code] || "").trim(),
        description: String(row[headersMap.description] || "").trim(),
        quantity_on_hand: String(row[headersMap.quantity_on_hand] || "").trim(),
        uom: String(row[headersMap.uom] || "").trim(),
      }));

      setFileData(rowsMap);
    }
  };

  const handleChangeFilters = (e) => {
    const { name, value, checked } = e.target;

    const newValue = name === "warehouse" ? value : checked;

    setFilters((prevState) => ({ ...prevState, [name]: newValue }));
  };

  const errorFallback = () => {
    setErrorInfo({ error: false, message: "" });
    setFileData([]);
    setFileProcessing(false);
    setComparisonResults([]);
  };

  const compareFileData = useCallback(() => {
    const comparisonData = fileData.map((item) => {
      const productData = productsStore.find(
        (pItem) => pItem?.item_code?.item_code === item.item_code,
      );

      const qbItemCode = item.item_code;

      const qbDescription = item.description;

      const productFound = Boolean(productData);

      const parStock = productData ? productData.par_stock : null;

      const qbOnHand = item ? parseFloat(item.quantity_on_hand || 0) : 0;

      const qbUOM = item ? String(item.uom || "") : "";

      const amountToRestock =
        qbOnHand <= 0 ? parStock || 0 : (parStock || 0) - qbOnHand;

      return {
        item_code: qbItemCode,
        description: qbDescription,
        qb_on_hand: qbOnHand,
        qb_uom: qbUOM,
        par_stock: parStock,
        amount_to_restock: amountToRestock,
        low_stock: qbOnHand <= (parStock || 0),
        exist: productFound,
        upc: productData?.upc || null,
        warehouse: productData?.warehouse || null,
      };
    });

    setComparisonResults(comparisonData);
    setFileProcessing(false);
  }, [fileData, productsStore]);

  useEffect(() => {
    if (!productsStore.length) {
      dispatch(fetch_products_data_action(productsStore));
    }
  }, [dispatch, productsStore]);

  useEffect(() => {
    if (fileData.length && productsStore.length && !errorInfo.error) {
      compareFileData();
    }
  }, [fileData, productsStore, errorInfo, compareFileData]);

  useEffect(() => {
    const warehousesGet = async () => {
      await backend.get(`${API_URL}warehouses?no_page=1`).then(({ data }) => {
        const dataMap = data.map((item) => ({ id: item.id, name: item.name }));
        setWarehouses(dataMap);
      });
    };
    warehousesGet();
  }, []);

  return (
    <Container>
      {!productsStore.length ? (
        <div align="center">
          <br />
          <CircularProgress />
          <div>
            Loading products <br />
          </div>
        </div>
      ) : !fileData.length && !fileProcessing ? (
        <UploadFile
          {...props}
          errorInfo={errorInfo}
          setErrorInfo={setErrorInfo}
          handleChangeData={handleChangeData}
        />
      ) : fileProcessing ? (
        <div align="center">
          <br />
          <CircularProgress />
          <div>Processing file data</div>
          {errorInfo.error ? (
            <>
              <br />
              <br />
              <div>
                <Typography component="h4" color="error">
                  {errorInfo.message}
                </Typography>
              </div>
              <div align="right">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => errorFallback()}
                >
                  Continue
                </Button>
              </div>
            </>
          ) : (
            <></>
          )}
        </div>
      ) : (
        <></>
      )}
      {!errorInfo.error && comparisonResults.length ? (
        <>
          <Typography component="h6" variant="h6" align="center">
            {`Quickbooks vs Software - Par Stock`}
          </Typography>
          <Typography variant="subtitle1">
            <strong>Filtering:</strong>
          </Typography>
          <Stack direction="row" alignItems="center" spacing={4} mb={2}>
            <FormControlLabel
              value="start"
              control={
                <Checkbox
                  name="lowStock"
                  value={filters.lowStock}
                  checked={filters.lowStock}
                  onChange={handleChangeFilters}
                  color="primary"
                />
              }
              label="Show low stock"
              labelPlacement="end"
            />
            <FormControlLabel
              value="start"
              control={
                <Checkbox
                  name="existing"
                  value={filters.existing}
                  checked={filters.existing}
                  onChange={handleChangeFilters}
                  color="primary"
                />
              }
              label="Show existing"
              labelPlacement="end"
            />
            <Autocomplete
              size="small"
              sx={{ minWidth: 300 }}
              options={warehouses}
              getOptionLabel={(option) => option.name || ""}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.name}
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} placeholder="Warehouse" size="small" />
              )}
              value={filters.warehouse}
              onChange={(e, newValue) =>
                handleChangeFilters({
                  target: { name: "warehouse", value: newValue },
                })
              }
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              disabled={warehouses.length === 0}
            />
          </Stack>
          <TableContainer style={{ maxHeight: "80vh" }}>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  <TableCell size="small">Item Code</TableCell>
                  <TableCell size="small">Description</TableCell>
                  <TableCell size="small">Product Exists</TableCell>
                  <TableCell size="small">QB U/M</TableCell>
                  <TableCell size="small">QB On Hand</TableCell>
                  <TableCell size="small">Par Stock</TableCell>
                  <TableCell size="small">Difference</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {comparisonResults
                  .filter((item, index) => {
                    let display = true;

                    if (filters.existing && !item.exist) {
                      display = false;
                    }

                    if (filters.lowStock && !item.low_stock) {
                      display = false;
                    }

                    if (
                      filters.warehouse &&
                      (!item.warehouse ||
                        item.warehouse?.id !== filters.warehouse?.id)
                    ) {
                      display = false;
                    }

                    return display;
                  })
                  .map((item, index) => (
                    <TableRow key={`row-${index}`} hover tabIndex={-1}>
                      <TableCell size="small">{item.item_code}</TableCell>
                      <TableCell size="small">{item.description}</TableCell>
                      <TableCell
                        size="small"
                        style={{
                          background: item.exist ? "lightgreen" : "lightyellow",
                          textAlign: "center",
                        }}
                      >
                        {item.exist ? <CheckIcon /> : <CloseIcon />}
                      </TableCell>
                      <TableCell
                        size="small"
                        style={{
                          background: !item.exist ? "lightyellow" : "inherit",
                          textAlign: "center",
                        }}
                      >
                        {item.exist ? item.qb_uom : <CloseIcon />}
                      </TableCell>
                      <TableCell
                        size="small"
                        style={{
                          background: !item.exist ? "lightyellow" : "inherit",
                          textAlign: "center",
                        }}
                      >
                        {item.exist ? item.qb_on_hand : <CloseIcon />}
                      </TableCell>
                      <TableCell
                        size="small"
                        style={{
                          background: !item.exist ? "lightyellow" : "inherit",
                          textAlign: "center",
                        }}
                      >
                        {item.exist ? (
                          item.par_stock ? (
                            item.par_stock
                          ) : (
                            <Link href={`/product/${item.upc}`} target="_blank">
                              <Tooltip title="No par stock set for this item | Edit product">
                                <IconButton aria-label="edit product">
                                  <OpenInNewIcon />
                                </IconButton>
                              </Tooltip>
                            </Link>
                          )
                        ) : (
                          <CloseIcon />
                        )}
                      </TableCell>
                      <TableCell
                        size="small"
                        style={{
                          background:
                            !item.exist || item.par_stock === null
                              ? "lightyellow"
                              : !item.low_stock
                                ? "lightgreen"
                                : "lightyellow",
                          textAlign: "center",
                        }}
                      >
                        {!item.exist || item.par_stock === null ? (
                          <CloseIcon />
                        ) : !item.low_stock ? (
                          <Tooltip
                            title={`Difference between QB on hand and ordered quantity is higher than par stock (${item.par_stock}).`}
                          >
                            <CheckIcon />
                          </Tooltip>
                        ) : (
                          <Tooltip
                            title={`Difference between QB on hand and ordered quantity is lower than par stock (${item.par_stock}). Difference: ${item.amount_to_restock}.`}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="center"
                              spacing={1}
                            >
                              <CloseIcon color="error" />
                              <Chip label={`${item.amount_to_restock}`} />
                            </Stack>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <></>
      )}
    </Container>
  );
};

export default ParStock;
